import { useCallback, useState } from 'react';
import {
  useInventoryArticles,
  useCreateInventoryArticle,
  useUpdateInventoryArticle,
  useDeleteInventoryArticle,
} from '../../../hooks/useInventoryArticles';
import {
  ITEMS_PER_PAGE,
  INITIAL_INVENTORY_ARTICLE_STATE,
} from '../constants/inventoryArticlesConstants';
import { filterInventoryArticles } from '../utils/inventoryArticlesUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

export const useInventoryArticlesTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(INITIAL_INVENTORY_ARTICLE_STATE);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [filters, setFilters] = useState({ status: 'all' });

  const { data: articles = [], isLoading } = useInventoryArticles();
  const createMutation = useCreateInventoryArticle();
  const updateMutation = useUpdateInventoryArticle();
  const deleteMutation = useDeleteInventoryArticle();

  const filterFn = useCallback(
    (collection, term) => filterInventoryArticles(collection, term, filters),
    [filters]
  );

  const {
    data: paginatedArticles,
    totalCount,
    totalPages,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    setPage,
  } = usePaginatedFilter({
    data: articles,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn,
  });

  const handleOpenDialog = () => {
    setSelectedArticle(INITIAL_INVENTORY_ARTICLE_STATE);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedArticle(INITIAL_INVENTORY_ARTICLE_STATE);
    setEditMode(false);
  };

  const handleEdit = (article) => {
    setSelectedArticle(article);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleCreate = async (payload) => {
    await createMutation.mutateAsync(payload);
  };

  const handleUpdate = async (uuid, payload) => {
    await updateMutation.mutateAsync({ id: uuid, data: payload });
  };

  const handleDeleteRequest = (article) => {
    setArticleToDelete(article);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!articleToDelete) return;
    await deleteMutation.mutateAsync(articleToDelete.uuid);
    setDeleteDialogOpen(false);
    setArticleToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setArticleToDelete(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const mutationsLoading =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;

  return {
    data: paginatedArticles,
    isLoading,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    filters,
    dialogOpen,
    editMode,
    selectedArticle,
    deleteDialogOpen,
    articleToDelete,
    mutationsLoading,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    handleOpenDialog,
    handleCloseDialog,
    handleEdit,
    handleCreate,
    handleUpdate,
    handleDeleteRequest,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
